import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import {
  ClipboardList,
  Loader2,
  Search,
  HardHat,
  Building2,
  DollarSign,
  Ruler,
  ChevronRight,
  Bell,
  MessageSquare,
  StickyNote,
  Flag,
  Paperclip,
  Sparkles,
  X,
} from "lucide-react";
import { Input } from "../../components/ui/input";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "../../components/ui/hover-card";
import { api } from "../../lib/api";
import { constructionApi } from "../../lib/copApi";
import { useAuthStore } from "../../store/authStore";

const STATUS_META = {
  green:  { color: "#059669", label: "Aktif" },
  yellow: { color: "#f59e0b", label: "Beklemede" },
  red:    { color: "#dc2626", label: "Sorunlu" },
  gray:   { color: "#9ca3af", label: "Belirsiz" },
};

const fmtMoney = (n) => {
  if (n === null || n === undefined || n === "") return "—";
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 0 }).format(v);
};

// Derive a 0–100 progress bar value from either the explicit
// `progress_percent` (manual override) or, when it's 0/missing, the ratio of
// completed milestones. Keeps the UI consistent across cards and detail view.
const deriveProgress = (row) => {
  if (row.progress_percent && row.progress_percent > 0) {
    return Math.min(100, Math.max(0, Number(row.progress_percent) || 0));
  }
  const ms = row.milestones || [];
  if (ms.length === 0) return 0;
  const done = ms.filter((m) => m.completed).length;
  return Math.round((done / ms.length) * 100);
};

function ProgressBar({ value, size = "sm" }) {
  const h = size === "lg" ? "h-3" : "h-1.5";
  const colour =
    value >= 80 ? "bg-emerald-500"
    : value >= 40 ? "bg-amber-500"
    : "bg-sky-500";
  return (
    <div className={`w-full overflow-hidden rounded-full bg-zinc-100 ${h}`}>
      <div
        className={`${h} ${colour} transition-all duration-500`}
        style={{ width: `${value}%` }}
      />
    </div>
  );
}

// Human-friendly one-liner for the tooltip. Empty buckets are hidden so
// the string stays compact ("3 yorum · 1 dosya" beats "3 yorum · 0
// milestone · 1 dosya"). Falls back to a generic sentence when the
// backend hasn't produced a breakdown yet (older cached responses).
const BUCKET_LABEL = {
  yorum: "yorum",
  milestone: "milestone",
  dosya: "dosya",
  diger: "diğer",
};
const formatBadgeBreakdown = (stats) => {
  if (!stats) return "";
  const bd = stats.breakdown || {};
  const parts = [];
  for (const key of ["yorum", "milestone", "dosya", "diger"]) {
    const n = Number(bd[key] || 0);
    if (n > 0) parts.push(`${n} ${BUCKET_LABEL[key]}`);
  }
  return parts.join(" · ");
};

// Per-action label + icon for the peek list. Falls back to a neutral
// "aktivite" for unknown actions so we never crash on new event types.
const ACTION_META = {
  comment_added:        { label: "yorum",              Icon: MessageSquare, tone: "text-sky-600 bg-sky-50" },
  note_mentioned:       { label: "not",                Icon: StickyNote,    tone: "text-amber-600 bg-amber-50" },
  milestone_completed:  { label: "milestone tamamlandı", Icon: Flag,        tone: "text-emerald-600 bg-emerald-50" },
  milestone_uncompleted:{ label: "milestone açıldı",   Icon: Flag,          tone: "text-zinc-600 bg-zinc-100" },
  file_uploaded:        { label: "dosya yüklendi",     Icon: Paperclip,     tone: "text-violet-600 bg-violet-50" },
  file_deleted:         { label: "dosya silindi",      Icon: Paperclip,     tone: "text-rose-600 bg-rose-50" },
  status_changed:       { label: "durum değişti",      Icon: Sparkles,      tone: "text-indigo-600 bg-indigo-50" },
  stage_changed:        { label: "aşama değişti",      Icon: Sparkles,      tone: "text-indigo-600 bg-indigo-50" },
  created:              { label: "oluşturuldu",        Icon: Sparkles,      tone: "text-zinc-600 bg-zinc-100" },
};
const DEFAULT_ACTION_META = { label: "aktivite", Icon: Sparkles, tone: "text-zinc-600 bg-zinc-100" };

// Turkish relative-time — dayjs.fromNow() defaults to English so we roll
// our own compact formatter ("2sa", "5dk", "az önce", "3g"). Kept
// internal to this page so we don't touch the shared `dates.js` helper.
const relativeTr = (iso) => {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const diff = Math.max(0, Date.now() - t);
  const sec = Math.round(diff / 1000);
  if (sec < 45) return "az önce";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}dk`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}sa`;
  const d = Math.round(hr / 24);
  if (d < 7) return `${d}g`;
  const w = Math.round(d / 7);
  if (w < 5) return `${w}h`;
  const mo = Math.round(d / 30);
  if (mo < 12) return `${mo}ay`;
  return `${Math.round(d / 365)}y`;
};

// Small preview string per activity: use `text` for comments, filename
// for file uploads, milestone label for milestone changes, or fall back
// to the action label.
const activityPreview = (a) => {
  const meta = a.meta || {};
  if (a.action === "comment_added" || a.action === "note_mentioned") {
    return a.text || "";
  }
  if (a.action === "file_uploaded" || a.action === "file_deleted") {
    return meta.filename || "";
  }
  if (a.action === "milestone_completed" || a.action === "milestone_uncompleted") {
    return meta.label || meta.key || "";
  }
  if (a.action === "status_changed") {
    return `${meta.from || "?"} → ${meta.to || "?"}`;
  }
  if (a.action === "stage_changed") {
    return `${meta.from || "—"} → ${meta.to || "—"}`;
  }
  return "";
};

function MinePeek({ rowId, mineStats }) {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const total = Number(mineStats?.total || 0);
  const breakdownLine = formatBadgeBreakdown(mineStats);

  // Lazy-load the last 5 activities the first time the popover opens.
  const load = useCallback(async () => {
    if (items !== null || loading) return;
    setLoading(true);
    try {
      const r = await api.get(
        `/construction/${rowId}/activities?mine_only=true&limit=5`
      );
      setItems(r.data || []);
    } catch (err) {
      console.error("Peek fetch failed:", err);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [rowId, items, loading]);

  return (
    <HoverCard openDelay={100} closeDelay={80} onOpenChange={(o) => o && load()}>
      <HoverCardTrigger asChild>
        <span
          data-testid={`tracking-card-mine-badge-${rowId}`}
          onClick={(e) => {
            e.stopPropagation();
            load();
          }}
          className="absolute right-3 top-3 inline-flex cursor-help items-center gap-1 rounded-full bg-violet-600 px-2 py-0.5 text-[10px] font-semibold text-white shadow-md ring-2 ring-white transition-transform hover:scale-110"
        >
          <Bell className="h-2.5 w-2.5" />
          {total}
        </span>
      </HoverCardTrigger>
      <HoverCardContent
        side="left"
        align="start"
        sideOffset={8}
        data-testid={`tracking-card-mine-peek-${rowId}`}
        onClick={(e) => e.stopPropagation()}
        className="w-80 border-zinc-200 bg-white p-0 shadow-xl"
      >
        {/* Header — dark strip with breakdown */}
        <div className="rounded-t-md bg-zinc-900 px-4 py-3 text-white">
          <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-violet-200">
            <Bell className="h-3 w-3" /> Bana yönelik ({total})
          </div>
          <div className="mt-0.5 text-xs font-medium">
            {breakdownLine || `${total} aktivite`}
          </div>
        </div>

        {/* Body — last-5 peek */}
        <div className="max-h-72 overflow-y-auto p-2">
          {loading || items === null ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />
            </div>
          ) : items.length === 0 ? (
            <div className="py-6 text-center text-[11px] text-zinc-400">
              Aktivite yok.
            </div>
          ) : (
            <ul className="space-y-1.5">
              {items.map((a) => {
                const meta = ACTION_META[a.action] || DEFAULT_ACTION_META;
                const Icon = meta.Icon;
                const preview = activityPreview(a);
                return (
                  <li
                    key={a.id}
                    data-testid={`peek-item-${a.id}`}
                    className="flex items-start gap-2 rounded-md px-2 py-1.5 hover:bg-zinc-50"
                  >
                    <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${meta.tone}`}>
                      <Icon className="h-3 w-3" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="truncate text-[11px] font-semibold text-zinc-800">
                          {a.actor_name || "Bilinmeyen"}
                        </span>
                        <span className="shrink-0 text-[10px] tabular-nums text-zinc-400">
                          {relativeTr(a.created_at)}
                        </span>
                      </div>
                      <div className="text-[11px] text-zinc-500">
                        {meta.label}
                        {preview && (
                          <span className="ml-1 text-zinc-700">
                            · <span className="line-clamp-1 inline">{preview}</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Footer — deep-link into detail */}
        <div className="border-t border-zinc-100 px-3 py-2 text-right">
          <Link
            to={`/app/cop/construction-tracking/${rowId}`}
            data-testid={`peek-open-detail-${rowId}`}
            onClick={(e) => e.stopPropagation()}
            className="text-[11px] font-semibold text-violet-600 hover:text-violet-800"
          >
            Detayı aç →
          </Link>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}

function TrackingCard({ row, users, mineStats, roleBadge, onOpen }) {
  const progress = deriveProgress(row);
  const assigned = users[row.assigned_to_user_id];
  const status = STATUS_META[row.status] || STATUS_META.gray;
  const completedCount = (row.milestones || []).filter((m) => m.completed).length;
  const totalCount = (row.milestones || []).length;
  const mentionCount = Number(mineStats?.total || 0);

  return (
    <button
      type="button"
      onClick={() => onOpen(row.id)}
      data-testid={`tracking-card-${row.id}`}
      className="group relative flex flex-col overflow-hidden rounded-xl border border-zinc-200 bg-white p-5 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-violet-300 hover:shadow-lg"
    >
      {/* Colored strip at the top matching the status */}
      <span
        className="absolute inset-x-0 top-0 h-1"
        style={{ backgroundColor: status.color }}
      />

      {/* "N bana yönelik" badge — top-right, only when > 0. HoverCard
          reveals the breakdown + a peek of the last 5 relevant activities.
          The activities list is lazy-loaded the first time the popover
          opens (`onOpenChange`) to keep the initial list-view fast. */}
      {mentionCount > 0 && <MinePeek rowId={row.id} mineStats={mineStats} />}

      {/* Header row: company + stand no */}
      <div className={`mb-3 flex items-start justify-between gap-2 ${mentionCount > 0 ? "pr-14" : ""}`}>
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-base font-semibold text-zinc-900">
            {row.company_name || "—"}
          </h3>
          <p className="mt-0.5 text-[11px] text-zinc-500">{row.country || "—"}</p>
        </div>
        {row.stand_no && (
          <span className="shrink-0 rounded-md bg-zinc-900 px-2 py-1 text-[11px] font-semibold text-white">
            #{row.stand_no}
          </span>
        )}
      </div>

      {/* Hub chip + role badge — role tells the current user whether
          they are the primary SORUMLU or one of the collaborators for
          this row. Super admin doesn't get a personal role since they
          see everything, so `roleBadge` is null in that case. */}
      <div className="mb-3 flex flex-wrap items-center gap-1.5">
        {row.hub_name && (
          <span className="inline-flex w-fit rounded-full bg-sky-50 px-2 py-0.5 text-[10px] font-medium text-sky-700">
            {row.hub_name}
          </span>
        )}
        {roleBadge && (
          <span
            data-testid={`role-badge-${row.id}`}
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${roleBadge.className}`}
            title={roleBadge.title}
          >
            {roleBadge.label}
          </span>
        )}
      </div>

      {/* Progress bar */}
      <div className="mb-3">
        <div className="mb-1 flex items-center justify-between text-[11px]">
          <span className="text-zinc-500">İlerleme</span>
          <span className="font-semibold tabular-nums text-zinc-900">
            {progress}%
          </span>
        </div>
        <ProgressBar value={progress} />
        <div className="mt-1 text-[10px] text-zinc-500">
          {completedCount}/{totalCount} kilometre taşı tamamlandı
        </div>
      </div>

      {/* Bottom stats — mini grid */}
      <div className="mt-auto grid grid-cols-3 gap-2 border-t border-zinc-100 pt-3 text-[11px]">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-zinc-400">Alan</div>
          <div className="font-medium text-zinc-900">
            {row.size_m2 ? `${row.size_m2} m²` : "—"}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-zinc-400">Bütçe</div>
          <div className="font-medium text-zinc-900">
            {row.stand_cost ? `${fmtMoney(row.stand_cost)} ${row.currency || ""}` : "—"}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-zinc-400">Sorumlu</div>
          <div className="truncate font-medium text-zinc-900">
            {assigned?.name || "—"}
          </div>
        </div>
      </div>

      <ChevronRight
        className="absolute right-4 top-1/2 hidden h-4 w-4 -translate-y-1/2 text-violet-500 group-hover:block"
      />
    </button>
  );
}

export default function CopConstructionTracking() {
  const navigate = useNavigate();
  const { user: currentUser } = useAuthStore();
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState({});
  const [mineStatsMap, setMineStatsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  // ?sorumlu=<user_id|none> — sorumlu bazlı filtre (dashboard'dan derin
  // bağlantıyla da gelinir).
  const sorumluFilter = searchParams.get("sorumlu");
  const setSorumluFilter = (val) => {
    const next = new URLSearchParams(searchParams);
    if (!val || sorumluFilter === val) next.delete("sorumlu");
    else next.set("sorumlu", val);
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [rowsData, userData, mineStatsData] = await Promise.all([
          constructionApi.list({}),
          api.get("/users?assignable=true").then((r) => r.data),
          api
            .get("/construction-stats/my-mentions")
            .then((r) => r.data)
            .catch(() => ({})),
        ]);
        setRows(rowsData);
        const userMap = {};
        for (const u of userData) userMap[u.id] = u;
        setUsers(userMap);
        setMineStatsMap(mineStatsData || {});
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    let list = rows;
    if (sorumluFilter === "none") {
      list = list.filter((r) => !r.assigned_to_user_id);
    } else if (sorumluFilter) {
      list = list.filter((r) => r.assigned_to_user_id === sorumluFilter);
    }
    if (!q) return list;
    const lower = q.toLowerCase();
    return list.filter(
      (r) =>
        (r.company_name || "").toLowerCase().includes(lower) ||
        (r.stand_no || "").toLowerCase().includes(lower) ||
        (r.contact_name || "").toLowerCase().includes(lower)
    );
  }, [rows, q, sorumluFilter]);

  // Sorumlu başına müşteri sayıları — chip şeridi için.
  const ownerChips = useMemo(() => {
    const counts = {};
    let unassigned = 0;
    for (const r of rows) {
      if (r.assigned_to_user_id) {
        counts[r.assigned_to_user_id] = (counts[r.assigned_to_user_id] || 0) + 1;
      } else {
        unassigned += 1;
      }
    }
    const chips = Object.entries(counts)
      .map(([id, count]) => ({ id, name: users[id]?.name || "Bilinmeyen", count }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name, "tr"));
    if (unassigned > 0) chips.push({ id: "none", name: "Atanmamış", count: unassigned });
    return chips;
  }, [rows, users]);

  // Overall KPIs for the strip on top.
  const stats = useMemo(() => {
    const totalBudget = rows.reduce((s, r) => s + (Number(r.stand_cost) || 0), 0);
    const totalArea = rows.reduce((s, r) => s + (Number(r.size_m2) || 0), 0);
    const avgProgress =
      rows.length === 0
        ? 0
        : Math.round(rows.reduce((s, r) => s + deriveProgress(r), 0) / rows.length);
    return { count: rows.length, totalBudget, totalArea, avgProgress };
  }, [rows]);

  return (
    <div
      className="mx-auto max-w-[1600px] p-4 sm:p-6 md:p-8"
      data-testid="cop-construction-tracking-page"
    >
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-sky-100 to-indigo-200">
            <ClipboardList className="h-5 w-5 text-sky-700" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">İnşaat Takip</h1>
            <p className="text-sm text-zinc-500">
              Her firmanın stant süreci — ilerleme, kilometre taşları ve teslim durumu.
            </p>
          </div>
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
          <Input
            placeholder="Firma, stant no, kontak…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 pr-9"
            data-testid="tracking-search"
          />
          {q && (
            <button
              type="button"
              onClick={() => setQ("")}
              aria-label="Aramayı temizle"
              title="Aramayı temizle"
              data-testid="tracking-search-clear"
              className="absolute right-2 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-700"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Overall KPI strip */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="rounded-xl border border-zinc-200 bg-white p-4">
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
            <HardHat className="h-3.5 w-3.5" /> Toplam proje
          </div>
          <div className="mt-1 text-2xl font-semibold text-zinc-900">
            {stats.count}
          </div>
        </div>
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-emerald-800">
            <Building2 className="h-3.5 w-3.5" /> Ortalama ilerleme
          </div>
          <div className="mt-1 text-2xl font-semibold text-emerald-900">
            {stats.avgProgress}%
          </div>
        </div>
        <div className="rounded-xl border border-sky-200 bg-sky-50 p-4">
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-sky-800">
            <Ruler className="h-3.5 w-3.5" /> Toplam alan
          </div>
          <div className="mt-1 text-2xl font-semibold text-sky-900">
            {fmtMoney(stats.totalArea)} m²
          </div>
        </div>
        <div className="rounded-xl border border-violet-200 bg-violet-50 p-4">
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-violet-800">
            <DollarSign className="h-3.5 w-3.5" /> Toplam bütçe
          </div>
          <div className="mt-1 text-2xl font-semibold text-violet-900">
            {fmtMoney(stats.totalBudget)}
          </div>
        </div>
      </div>

      {/* Sorumlu chip şeridi — kim kaç müşteriyle ilgileniyor */}
      {!loading && ownerChips.length > 0 && (
        <div
          className="mb-6 flex flex-wrap items-center gap-2"
          data-testid="tracking-owner-chips"
        >
          <span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400">
            Sorumlular:
          </span>
          <button
            type="button"
            onClick={() => setSorumluFilter(null)}
            data-testid="owner-chip-all"
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              !sorumluFilter
                ? "bg-zinc-900 text-white"
                : "bg-white text-zinc-600 ring-1 ring-zinc-200 hover:bg-zinc-50"
            }`}
          >
            Tümü · {rows.length}
          </button>
          {ownerChips.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setSorumluFilter(c.id)}
              data-testid={`owner-chip-${c.id}`}
              title={`${c.name} — ${c.count} müşteri`}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                sorumluFilter === c.id
                  ? "bg-violet-600 text-white"
                  : c.id === "none"
                  ? "bg-amber-50 text-amber-800 ring-1 ring-amber-200 hover:bg-amber-100"
                  : "bg-white text-zinc-700 ring-1 ring-zinc-200 hover:bg-violet-50 hover:text-violet-700"
              }`}
            >
              {c.id !== "none" && (
                <span
                  className={`flex h-4 w-4 items-center justify-center rounded-full text-[9px] font-semibold ${
                    sorumluFilter === c.id
                      ? "bg-white/25 text-white"
                      : "bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white"
                  }`}
                >
                  {c.name.charAt(0).toUpperCase()}
                </span>
              )}
              {c.name}
              <span
                className={`rounded-full px-1.5 text-[10px] font-semibold tabular-nums ${
                  sorumluFilter === c.id ? "bg-white/25" : "bg-zinc-100 text-zinc-600"
                }`}
              >
                {c.count}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Cards grid */}
      {loading ? (
        <div className="flex items-center justify-center rounded-xl border border-zinc-200 bg-white py-24">
          <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-zinc-300 bg-white py-16 text-center text-sm text-zinc-500">
          Kayıt bulunamadı.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filtered.map((r) => {
            // Role badge: shown to everyone (including super admins so
            // they can spot their personal involvement in the tenant-
            // wide view). Priority: primary SORUMLU > company manager >
            // per-milestone collaborator > null.
            let roleBadge = null;
            const managed = new Set(currentUser?.assigned_company_ids || []);
            if (currentUser?.id) {
              if (r.assigned_to_user_id === currentUser.id) {
                roleBadge = {
                  label: "Sorumlusun",
                  className: "bg-violet-100 text-violet-800 ring-1 ring-violet-200",
                  title: "Bu projede ana sorumlusun",
                };
              } else if (r.company_id && managed.has(r.company_id)) {
                roleBadge = {
                  label: "Şirket yöneticisi",
                  className: "bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200",
                  title: "Bu şirketin yöneticisi olarak görüyorsun",
                };
              } else if (
                (r.milestones || []).some((m) =>
                  (m.assigned_user_ids || []).includes(currentUser.id)
                )
              ) {
                roleBadge = {
                  label: "Kollaboratör",
                  className: "bg-sky-100 text-sky-800 ring-1 ring-sky-200",
                  title: "Bir veya daha fazla aşamaya kollaboratör olarak eklendin",
                };
              }
            }
            return (
              <TrackingCard
                key={r.id}
                row={r}
                users={users}
                mineStats={mineStatsMap[r.id]}
                roleBadge={roleBadge}
                onOpen={(id) => navigate(`/app/cop/construction-tracking/${id}`)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
